/**
 * Loading, empty and error states.
 *
 * Sections 13.42–13.43 require all three on every screen that fetches data, and
 * M2 makes that a completion criterion. They live together because they are the
 * same decision: what a screen shows when it has no rows to show.
 *
 * The rule a reviewer can check quickly:
 *
 *   isPending  -> <LoadingState />
 *   isError    -> <ErrorState error={error} onRetry={refetch} />
 *   no rows    -> <EmptyState ... />   with an action where one makes sense
 *   otherwise  -> the data
 *
 * Guard on `isPending`, NOT `isLoading`. In React Query v5 `isLoading` means
 * `isPending && isFetching`, so it drops to false in the gap between a failed
 * attempt and its retry - while there is still no data and no error. A chain
 * written with `isLoading` falls through to the empty branch during that gap and
 * tells a member of staff there are no records, when the truth is that the
 * request has not finished. `isPending` stays true across the whole window.
 *
 * The gap is not always brief: React Query pauses retries while the window is
 * unfocused, so a staff member who starts a search and switches tabs can come
 * back to a screen that has been sitting in that state. This was seen on the M0
 * dashboard, which claimed the database had no reference data while the API was
 * simply unreachable.
 *
 * `EmptyState` takes a `message`, not just a title, because "No volunteers" leaves
 * a member of staff wondering whether the list is broken or genuinely empty.
 */

import type { ReactNode } from 'react';
import { ApiRequestError } from '../../services/api/client';
import { Button } from './Button';
import styles from './States.module.css';

export interface LoadingStateProps {
  label?: string;
}

export function LoadingState({ label = 'Loading…' }: LoadingStateProps) {
  return (
    <div className={styles.container} role="status" aria-live="polite">
      <span className={styles.spinner} aria-hidden="true" />
      <p className={styles.message}>{label}</p>
    </div>
  );
}

export interface EmptyStateProps {
  title: string;
  message: string;
  action?: ReactNode;
}

export function EmptyState({ title, message, action }: EmptyStateProps) {
  return (
    <div className={styles.container}>
      <p className={styles.title}>{title}</p>
      <p className={styles.message}>{message}</p>
      {action && <div className={styles.action}>{action}</div>}
    </div>
  );
}

export interface ErrorStateProps {
  error: unknown;
  /**
   * What the failure means *here*, when that is not obvious from the API's
   * message. A failed volunteer search is the case this was added for: the
   * generic wording is true and useless, while "this is not a list of who is
   * available" is what stops somebody adding a person already on file.
   */
  message?: string;
  onRetry?: () => void;
}

export function ErrorState({ error, message, onRetry }: ErrorStateProps) {
  return (
    <div className={styles.container} role="alert">
      <p className={styles.title}>Something went wrong</p>
      {message && <p className={styles.message}>{message}</p>}
      <p className={styles.message}>{describe(error)}</p>
      {onRetry && (
        <div className={styles.action}>
          <Button onClick={onRetry}>Try again</Button>
        </div>
      )}
    </div>
  );
}

/**
 * Turn a thrown value into a sentence for a member of staff.
 *
 * An `ApiRequestError` already carries a message the API wrote for a human
 * (Section 5.10), so it is used verbatim. Anything else is a frontend bug, and
 * showing its raw text would just be confusing.
 */
function describe(error: unknown): string {
  if (error instanceof ApiRequestError) return error.message;
  if (error instanceof Error && error.message) return error.message;
  return 'An unexpected error occurred. Please try again.';
}
